import { useState } from "react";
import { BookCheck, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Money } from "@/components/common";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { toast } from "sonner";

function CheckTable({ rows }) {
  return (
    <div className="max-h-72 overflow-auto border border-slate-200 rounded-lg" data-testid="post-check-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-500 uppercase sticky top-0"><tr><th className="px-2 py-1.5 text-left">ID</th><th className="px-2 py-1.5 text-left">Name</th><th className="px-2 py-1.5 text-right">Deduction</th><th className="px-2 py-1.5 text-left">Ledger</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.code} className={`border-t ${r.error ? "bg-red-50" : ""}`} data-testid={`post-check-row-${r.code}`}>
              <td className="px-2 py-1.5 font-semibold">{r.code}</td>
              <td className="px-2 py-1.5">{r.name || r.file_name}</td>
              <td className="px-2 py-1.5 text-right"><Money value={r.deduction} /></td>
              <td className={`px-2 py-1.5 ${r.error ? "text-red-700" : "text-slate-600"}`}>{r.error || <>{inr(r.current_balance)} → <b>{inr(r.final_balance)}</b></>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PostToLedgers({ payload }) {
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [key] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`));

  const open = async () => {
    setBusy(true);
    try { const { data } = await api.post("/payroll-statement/post-check", payload); setCheck(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll-statement/post", { ...payload, batch_key: key });
      setDone(data); setCheck(null);
      toast.success(`Posted ${data.recorded} deductions (${inr(data.amount)}) to ledgers`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (done) {
    return <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700" data-testid="post-ledgers-done"><CheckCircle2 className="h-4 w-4" /> Posted {done.recorded} deductions to ledgers for {done.payroll_period}</div>;
  }
  const blocked = check && check.problems > 0;
  return (
    <>
      <Button variant="outline" className="border-blue-800 text-blue-800" onClick={open} disabled={busy} data-testid="post-ledgers-btn">
        {busy && !check ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookCheck className="h-4 w-4" />} Post to Ledgers
      </Button>
      <ConfirmDialog open={!!check} onOpenChange={(o) => !o && setCheck(null)} busy={busy} testid="post-ledgers-dialog"
        title={blocked ? "Posting blocked" : "Post deductions to employee ledgers?"}
        confirmText={blocked ? "Close" : `Post ${check?.rows.length || 0} deductions`}
        onConfirm={blocked ? () => setCheck(null) : post}
        description={check && (blocked
          ? `${check.problems} row${check.problems > 1 ? "s" : ""} must be fixed before anything is posted.${check.already_posted ? " Deductions already posted for this payroll period cannot be posted again." : ""}`
          : `This records an Advance Deduction dated ${fmtDate(check.statement_date)} with reference "${check.payroll_period}" in each employee's ledger. Total ${inr(check.total)}. Employees with ₹0.00 deduction are skipped.`)}>
        {check && (
          <div className="space-y-2">
            {blocked && <div className="flex items-center gap-2 text-sm text-red-700" data-testid="post-ledgers-blocked"><AlertTriangle className="h-4 w-4" /> Nothing has been posted.</div>}
            <CheckTable rows={check.rows} />
          </div>
        )}
      </ConfirmDialog>
    </>
  );
}
